import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import styled from 'styled-components';

import aboutHeaderImage from '../../assets/hscan-landing/about_header.jpg';
import easilyAtHomeImage from '../../assets/hscan-landing/easily_at_home.jpg';
import goodQualityImage from '../../assets/hscan-landing/good_quality.jpg';
import hpacsSolutionImage from '../../assets/hscan-landing/hpacs_solution_new.png';
import lowCostImage from '../../assets/hscan-landing/low_cost.jpg';
import noLimitWithCloud from '../../assets/hscan-landing/no_limit_with_cloud.jpg';
import rightArrowIcon from '../../assets/icons/icon_arrow-right.svg';
import endToEndIcon from '../../assets/icons/icon_end-to-end.png';
import infrastructureIcon from '../../assets/icons/icon_infrastructure.png';
import reliabilityIcon from '../../assets/icons/icon_reliability.png';
import resiliencyIcon from '../../assets/icons/icon_resiliency.png';
import { ColoredIcon } from '../../common/components/Icon';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

const Section1 = styled.section`
  padding-top: 115px;
  padding-right: 27px;
  padding-bottom: 110px;
  padding-left: 27px;
  background: var(--HPACS_BG_grey, #f5f5f5);

  ${p =>
    p.theme.isMobile &&
    `
    padding-top: 32px;
    padding-bottom: 15px;
    `}
`;

const Section1TextWrapper = styled.div`
  background-image: url(${hpacsSolutionImage});
  background-size: min(600px, 100vw - 550px - 5px);
  background-position: center right;
  background-repeat: no-repeat;

  @media (max-width: 873px) {
    // 있는게 추하다 싶을 때 제거함
    background-image: none;
  }

  ${p =>
    p.theme.isMobile &&
    `
    display: flex;
    justify-content: center;
    `}
`;
const Section1Heading = styled.h2`
  margin: 0;
  color: var(--HScan_blue_grey, #00025a);
  word-break: keep-all;
`;
const Section1PreText = styled.span`
  font-size: 16px;
  font-weight: 500;
`;
const Section1EmphasizedTextWrapper = styled.div`
  font-size: 65px;
  font-weight: 900;
  margin-top: 20px;
  margin-bottom: 40px;
`;
const Section1EmphasizedText = styled.span`
  color: var(--HScan_Light_Blue, #2e5be0);
`;
const Section1Text = styled.p`
  margin: 0;
  max-width: 545px;
  font-size: 16px;
  line-height: 30px;
  color: var(--HScan_dark_blue, #16172c);
`;
const Section1StrongText = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
`;
const Section2 = styled.section`
  padding-top: 90px;
  padding-right: 27px;
  padding-bottom: 62px;
  padding-left: 27px;
  background-color: ${p => p.theme.colors.white};

  ${p =>
    p.theme.isMobile &&
    `
    padding-top: 32px;
    padding-bottom: 15px;
    background-color: #f5f5f5;
    `}
`;

const Section2PhraseWrapper = styled.div`
  text-align: center;
`;
const Section2PhraseHeading = styled.h2`
  margin: 0;
  font-size: 39px;
  font-weight: 500;
  color: var(--HScan_blue_grey, #00025a);
  word-break: keep-all;

  @media (max-width: 1129px) {
    font-size: 35px;
  }

  ${p =>
    p.theme.isMobile &&
    `
    font-size: 16px;
    font-weight: 600;
    `}
`;
const Section2PhraseText = styled.p`
  margin: 0;
  margin-top: 50px;
  font-size: 16px;
  line-height: 30px;
  word-break: keep-all;
  color: var(--HScan_dark_blue, #16172c);

  ${p =>
    p.theme.isMobile &&
    `
    margin-top: 24px;
    text-align: left;
    `}
`;

const Attributes = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  margin-top: 50px;
  word-break: keep-all;

  ${p =>
    p.theme.isMobile &&
    `
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    align-items: normal;
    justify-content: normal;
    gap: 14px;
    `}
`;
const AttributeWrapper = styled.div`
  flex: 1;
  max-width: 262px;
  box-sizing: border-box;
  padding: 26px;

  @media (max-width: 1129px) {
    padding: 14px;
  }

  ${p =>
    p.theme.isMobile &&
    `
    background: white;
    max-width: none;
    `}
`;
const AttributeIconWrapper = styled.div`
  height: 94px;
  line-height: 0;
  text-align: center;

  ${p =>
    p.theme.isMobile &&
    `
    height: 47px;
    `}
`;
const AttributeIcon = styled.img`
  ${p =>
    p.theme.isMobile &&
    `
    max-width: 44px;
    `}
`;
const AttributeName = styled.h3`
  margin: 0;
  margin-top: 30px;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  color: var(--HScan_Light_Blue, #2e5be0);
`;
const AttributeHeading = styled.p`
  margin: 0;
  margin-top: 30px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  color: var(--HScan_dark_blue, #16172c);
  word-break: keep-all;

  ${p =>
    p.theme.isMobile &&
    `
    font-size: 13px;
    `}
`;
const AttributeText = styled.p`
  margin: 0;
  margin-top: 20px;
  font-size: 14px;
  line-height: 26px;
  word-break: keep-all;
  color: var(--HPACS_Text_Light_Grey, #a3a6ab);
`;

const SECTION3_CARDS_MOBILE_HORIZONTAL_PADDING = 27;
const SECTION3_CARDS_NARROW_THRESHOLD = 800; // 찌부러지지 않은 정도 (카드 높이가 더 높지 않으며 높이가 300 이상이 되지 않는 경우)

const Section3 = styled.section`
  padding-top: 90px;
  padding-right: ${SECTION3_CARDS_MOBILE_HORIZONTAL_PADDING}px;
  padding-bottom: 78px;
  padding-left: ${SECTION3_CARDS_MOBILE_HORIZONTAL_PADDING}px;
  background: var(--HPACS_BG_grey, #f5f5f5);

  ${p =>
    p.theme.isMobile &&
    `
    padding-top: 32px;
    padding-bottom: 15px;
    `}
`;

const Section3PhraseWrapper = styled.div`
  text-align: center;
`;
const Section3PhraseHeading = styled.h2`
  margin: 0;
  font-size: 39px;
  font-weight: 500;
  color: var(--HScan_blue_grey, #00025a);
  word-break: keep-all;

  @media (max-width: 1129px) {
    font-size: 35px;
    margin-left: -16px;
    margin-right: -16px;
  }

  ${p =>
    p.theme.isMobile &&
    `
    font-size: 16px;
    font-weight: 600;
    `}
`;
const Section3PhraseText = styled.p`
  margin: 0;
  margin-top: 40px;
  font-size: 16px;
  font-weight: 400;
  color: var(--HScan_dark_blue, #16172c);

  ${p =>
    p.theme.isMobile &&
    `
    display: none;
    `}
`;

const Cards = styled.div`
  display: grid;
  // 2x1
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  column-gap: 58px;
  row-gap: 40px;
  margin-top: 60px;
  word-break: keep-all;
  container-type: inline-size;

  // 4x1
  @media (max-width: ${SECTION3_CARDS_NARROW_THRESHOLD - 1}px) {
    display: flex;
    flex-direction: column;
    row-gap: 22px;
    align-items: center;
    margin-top: 28px;
  }
`;
const Card = styled.div`
  display: flex;
  max-width: 530px;
  width: 100%;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  container-type: inline-size;

  @container (width < 344px) {
    flex-direction: column;
  }
  @supports not (container-type: inline-size) {
    @media (max-width: 397px) {
      flex-direction: column;
    }
  }
`;
const CardLeftImage = styled.img`
  min-width: 112px;
  width: calc((100cqw - 394px) * 40 / 136 + 112px);
  max-width: 152px;
  object-fit: cover;

  @container (width < 344px) {
    max-width: none;
    width: 100%;
    height: 100px;
    object-position: center !important;
  }
  @supports not (container-type: inline-size) {
    @media (max-width: 397px) {
      max-width: none;
      width: 100%;
      height: 100px;
      object-position: center !important;
    }
  }
`;
const CardRight = styled.div`
  flex: 1;
  padding-top: 50px;
  padding-right: 60px;
  padding-bottom: 50px;
  padding-left: 40px;

  @container (width < 509px) {
    padding-top: 28px;
    padding-right: 26px;
    padding-bottom: 26px;
    padding-left: 20px;
  }
  @supports not (container-type: inline-size) {
    @media (max-width: 397px) {
      padding-top: 28px;
      padding-right: 26px;
      padding-bottom: 26px;
      padding-left: 20px;
    }
  }
`;
const CardHeading = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--HScan_blue_grey, #00025a);
`;
const CardContent = styled.ul`
  margin: 0;
  margin-top: 26px;
  padding-left: 10px;
`;
const CardContentItem = styled.li`
  font-size: 14px;
  line-height: 21px;
  color: var(--HPACS_Text_Light_Grey, #a3a6ab);

  & + & {
    margin-top: 10px;
  }
`;

const SectionContent = styled.div`
  margin: 0 auto; /* center */
  max-width: 1130px;
`;

const MobileSection0 = styled.div`
  position: relative;
  height: 247px;
  padding-top: 56px;
  padding-left: 35%;
  font-size: 19px;
  font-size: calc(19px + max(((100vw - 454px) / (650 - 454) * 10), 0px));
  /* 454px 이미지 안 변하는 수준 */
  font-weight: 600;
  line-height: 34px;
  line-height: calc(34px + max(((100vw - 454px) / (650 - 454) * 20), 0px));
  color: #00025a;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-image: url(${aboutHeaderImage});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center top;
    opacity: 0.7;
  }
`;

const MobileSection0Text = styled.div`
  width: fit-content;
  transform: translateX(-50%);
`;

const MobileSection0Underline = styled.span`
  text-decoration: underline;
  text-decoration-color: #2e5be0aa;
  text-decoration-thickness: 9px;
  text-underline-offset: -2px;
`;

const FaqButton = styled(Link)`
  display: block;
  margin: 0 auto;
  position: relative;
  margin-top: ${p => (p.theme.isMobile ? '55px' : '73px')};
  width: ${p => (p.theme.isMobile ? '245px' : '562px')};
  box-sizing: border-box;
  padding: 15px;
  border-radius: 50px;
  text-align: center;
  background: #00025a;
  cursor: pointer;
  text-decoration: none;
`;
const FaqButtonText = styled.span`
  font-size: ${p => (p.theme.isMobile ? '12px' : '24px')};
  color: white;
  font-weight: 700;
`;
const FaqButtonIcon = styled(ColoredIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: ${p => (p.theme.isMobile ? '25px' : '84px')};
`;

export const About = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'features.about' });

  const windowSize = useContext(WindowSizeContext);
  const isMobile = windowSize === WindowSize.MOBILE;

  return (
    <>
      {isMobile && (
        <MobileSection0>
          <MobileSection0Text>
            <Trans
              t={t}
              i18nKey="section1.mobilePreText"
              components={[<br />, <MobileSection0Underline />]}
            />
          </MobileSection0Text>
        </MobileSection0>
      )}
      <Section1>
        <SectionContent>
          <Section1TextWrapper>
            {!isMobile && (
              <Section1Heading>
                <Section1PreText>{t('section1.preText')}</Section1PreText>
                <Section1EmphasizedTextWrapper>
                  <Trans
                    t={t}
                    i18nKey="section1.emphasized"
                    components={[<Section1EmphasizedText />, <br />]}
                  />
                </Section1EmphasizedTextWrapper>
              </Section1Heading>
            )}
            <Section1Text>
              <Trans
                t={t}
                i18nKey="section1.text"
                components={[<Section1StrongText />]}
              />
            </Section1Text>
          </Section1TextWrapper>
        </SectionContent>
      </Section1>
      <Section2>
        <SectionContent>
          <Section2PhraseWrapper>
            <Section2PhraseHeading>
              {t('section2.heading')}
            </Section2PhraseHeading>
            <Section2PhraseText>{t('section2.text')}</Section2PhraseText>
          </Section2PhraseWrapper>
          <Attributes>
            <AttributeWrapper>
              <AttributeIconWrapper>
                <AttributeIcon
                  width="85px"
                  src={endToEndIcon}
                  alt={`${t('section2.attributes.0.alt')}`}
                />
              </AttributeIconWrapper>
              <AttributeName>{t('section2.attributes.0.name')}</AttributeName>
              <AttributeHeading>
                {t('section2.attributes.0.heading')}
              </AttributeHeading>
              <AttributeText>{t('section2.attributes.0.text')}</AttributeText>
            </AttributeWrapper>
            <AttributeWrapper>
              <AttributeIconWrapper>
                <AttributeIcon
                  width={85}
                  src={resiliencyIcon}
                  alt={`${t('section2.attributes.1.alt')}`}
                />
              </AttributeIconWrapper>
              <AttributeName>{t('section2.attributes.1.name')}</AttributeName>
              <AttributeHeading>
                {t('section2.attributes.1.heading')}
              </AttributeHeading>
              <AttributeText>{t('section2.attributes.1.text')}</AttributeText>
            </AttributeWrapper>
            <AttributeWrapper>
              <AttributeIconWrapper>
                <AttributeIcon
                  width={85}
                  src={reliabilityIcon}
                  alt={`${t('section2.attributes.2.alt')}`}
                />
              </AttributeIconWrapper>
              <AttributeName>{t('section2.attributes.2.name')}</AttributeName>
              <AttributeHeading>
                {t('section2.attributes.2.heading')}
              </AttributeHeading>
              <AttributeText>{t('section2.attributes.2.text')}</AttributeText>
            </AttributeWrapper>
            <AttributeWrapper>
              <AttributeIconWrapper>
                <AttributeIcon
                  width={80}
                  src={infrastructureIcon}
                  alt={`${t('section2.attributes.3.alt')}`}
                />
              </AttributeIconWrapper>
              <AttributeName>{t('section2.attributes.3.name')}</AttributeName>
              <AttributeHeading>
                {t('section2.attributes.3.heading')}
              </AttributeHeading>
              <AttributeText>{t('section2.attributes.3.text')}</AttributeText>
            </AttributeWrapper>
          </Attributes>
        </SectionContent>
      </Section2>
      <Section3>
        <SectionContent>
          <Section3PhraseWrapper>
            <Section3PhraseHeading>
              {t('section3.heading')}
            </Section3PhraseHeading>
            <Section3PhraseText>{t('section3.text')}</Section3PhraseText>
          </Section3PhraseWrapper>
          <Cards>
            <Card>
              <CardLeftImage
                src={easilyAtHomeImage}
                alt={t('section3.cards.0.alt') as string}
              />
              <CardRight>
                <CardHeading>{t('section3.cards.0.heading')}</CardHeading>
                <CardContent>
                  <CardContentItem>
                    {t('section3.cards.0.contents.0')}
                  </CardContentItem>
                  <CardContentItem>
                    {t('section3.cards.0.contents.1')}
                  </CardContentItem>
                </CardContent>
              </CardRight>
            </Card>
            <Card>
              <CardLeftImage
                src={noLimitWithCloud}
                alt={t('section3.cards.2.alt') as string}
              />
              <CardRight>
                <CardHeading>{t('section3.cards.2.heading')}</CardHeading>
                <CardContent>
                  <CardContentItem>
                    {t('section3.cards.2.contents.0')}
                  </CardContentItem>
                  <CardContentItem>
                    {t('section3.cards.2.contents.1')}
                  </CardContentItem>
                </CardContent>
              </CardRight>
            </Card>
          </Cards>
          <FaqButton to="/faq">
            <FaqButtonText>{t('section3.gotoFaq')}</FaqButtonText>
            <FaqButtonIcon
              width="9px"
              height="20px"
              imageUrl={rightArrowIcon}
              imageWidth="9px"
              color="#ffffff"
            />
          </FaqButton>
        </SectionContent>
      </Section3>
    </>
  );
};
